import { useState, useEffect, useRef } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { docMgmtI18n } from '../../i18n';
import { Editor } from '@tinymce/tinymce-react';
import html2pdf from 'html2pdf.js';
import { TinyMCE_ApiKey } from '../../../../config';
import { getPagedCasesByUser } from '../../../../services/caseServices';
import {
  getDocumentConfigByService,
  getDocumentSendAutoSelection,
  generateTemplatePreview,
  createDocumentSend,
} from '../../../../services/documentManagementServices';

const EDITOR_LANG_MAP = { 'es-CL': 'es', 'en-US': 'en', 'pt-BR': 'pt_BR' };

const ModalNewSending = ({
  DesignSystemComponents,
  show,
  onClose,
  serviceOptions = [],
  areaOptions = [],
  startGlobalLoading,
  stopGlobalLoading,
  onSuccess,
  userId,
}) => {
  const { Modal, Input, Dropdown, ComboBox, Skeleton, Button } =
    DesignSystemComponents;

  const { t, language: dmLanguage } = useScopedTranslation(docMgmtI18n);

  const editorRef = useRef(null);

  const [sendingName, setSendingName] = useState('');
  const [sendingCase, setSendingCase] = useState(null);
  const [sendingService, setSendingService] = useState(null);
  const [sendingArea, setSendingArea] = useState(null);
  const [sendingDoc, setSendingDoc] = useState(null);

  const [documentOptions, setDocumentOptions] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const [caseOptions, setCaseOptions] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [caseSearch, setCaseSearch] = useState('');

  const [editorContent, setEditorContent] = useState('');
  const [loadingEditor, setLoadingEditor] = useState(true);

  const [showConfirm, setShowConfirm] = useState(false);

  const resetForm = () => {
    setSendingName('');
    setSendingCase(null);
    setSendingService(null);
    setSendingArea(null);
    setSendingDoc(null);

    setDocumentOptions([]);
    setCaseOptions([]);
    setCaseSearch('');

    setEditorContent('');
    editorRef.current?.setContent('');

    setLoadingDocs(false);
    setLoadingCases(false);
    setLoadingEditor(true);

    setShowConfirm(false);
  };

  useEffect(() => {
    if (!show) resetForm();
  }, [show]);

  useEffect(() => {
    if (!show) return;

    setEditorContent('');

    const loadInitial = async () => {
      try {
        setLoadingCases(true);

        const filters = {
          UserId: userId,
          SearchLegalCases: '',
          Case: '',
          MyCases: 'mis causas',
          Courts: [],
          StartDate: null,
          EndDate: null,
          Role: '',
          Client: '',
          Process: [],
          States: [],
          Lawyers: [],
        };

        const resp = await getPagedCasesByUser(1, 50, filters);

        setCaseOptions(
          resp.cases.map((c) => ({
            label: `${c.title} — Rol ${c.code}`,
            value: c.id,
            raw: c,
          }))
        );
      } catch {
        setCaseOptions([]);
      } finally {
        setLoadingCases(false);
      }
    };

    loadInitial();
  }, [show]);

  useEffect(() => {
    if (!caseSearch.trim()) return;

    const timeout = setTimeout(async () => {
      try {
        setLoadingCases(true);

        const filters = {
          UserId: userId,
          SearchLegalCases: caseSearch,
          Case: caseSearch,
          MyCases: 'todos',
          Courts: [],
          StartDate: null,
          EndDate: null,
          Role: '',
          Client: '',
          Process: [],
          States: [],
          Lawyers: [],
        };

        const resp = await getPagedCasesByUser(1, 50, filters);

        setCaseOptions(
          resp.cases.map((c) => ({
            label: `${c.title} — Rol ${c.code}`,
            value: c.id,
            raw: c,
          }))
        );
      } catch {
        setCaseOptions([]);
      } finally {
        setLoadingCases(false);
      }
    }, 350);

    return () => clearTimeout(timeout);
  }, [caseSearch]);

  useEffect(() => {
    if (!sendingCase?.value) return;

    const loadAutoSelection = async () => {
      try {
        const resp = await getDocumentSendAutoSelection.getAutoSelection(
          sendingCase.value
        );

        const { businessAreaId, serviceId } = resp;

        const foundArea = areaOptions.find((a) => a.value === businessAreaId);
        const foundService = serviceOptions.find((s) => s.value === serviceId);

        if (foundArea) setSendingArea(foundArea);
        if (foundService) setSendingService(foundService);
      } catch {}
    };

    loadAutoSelection();
  }, [sendingCase]);

  useEffect(() => {
    if (!sendingService?.value) {
      setDocumentOptions([]);
      setSendingDoc(null);
      return;
    }

    const loadDocs = async () => {
      try {
        setLoadingDocs(true);

        const resp = await getDocumentConfigByService.getByService(
          sendingService.value
        );

        setDocumentOptions(
          resp.map((d) => ({
            label: d.name,
            value: d.id,
            raw: d,
          }))
        );
      } catch {
        setDocumentOptions([]);
      } finally {
        setLoadingDocs(false);
      }
    };

    loadDocs();
  }, [sendingService]);

  useEffect(() => {
    if (!sendingDoc?.value) return;

    const loadTemplate = async () => {
      try {
        const payload = {
          documentConfigurationId: sendingDoc.value,
          userId,
          caseName: sendingCase?.raw?.title ?? '',
          serviceName: sendingService?.label ?? '',
          businessAreaName: sendingArea?.label ?? '',
          caseId: sendingCase?.value || 0,
        };

        const html = await generateTemplatePreview.getTemplate(payload);

        setEditorContent(html || '');
        editorRef.current?.setContent(html || '');
      } catch {
        setEditorContent('');
        editorRef.current?.setContent('');
      }
    };

    loadTemplate();
  }, [sendingDoc, sendingCase, sendingService, sendingArea, userId]);

  const createAndSend = async () => {
    startGlobalLoading();

    try {
      const html = editorRef.current?.getContent() || '';
      if (!html.trim()) {
        alert(t('dm_modal_emptyContent'));
        return;
      }

      const pdfBlob = await html2pdf()
        .from(html)
        .set({
          margin: 10,
          filename: `${sendingName}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .outputPdf('blob');

      const pdfFile = new File([pdfBlob], `${sendingName}.pdf`, {
        type: 'application/pdf',
      });

      await createDocumentSend.create({
        file: pdfFile,
        name: sendingName,
        caseId: sendingCase.value,
        serviceId: sendingService.value,
        businessAreaId: sendingArea.value,
        documentConfigurationId: sendingDoc.value,
        userId,
        createdAt: new Date()
          .toLocaleString('sv-SE', { timeZone: 'America/Santiago' })
          .replace(' ', 'T'),
      });

      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      stopGlobalLoading();
    }
  };

  const handleCreateSending = () => {
    const isAutomatic = sendingDoc?.raw?.deliveryTypeId === 2;

    if (isAutomatic) {
      setShowConfirm(true);
      return;
    }

    createAndSend();
  };

  return (
    <Modal
      title={showConfirm ? t('dm_modal_newTitleConfirm') : t('dm_modal_newTitle')}
      show={show}
      onClose={onClose}
      width={showConfirm ? 520 : 1200}
      height={showConfirm ? 'auto' : '80vh'}
      primaryBtnTxt={showConfirm ? null : t('dm_modal_create')}
      primaryBtnAction={showConfirm ? null : handleCreateSending}
      disabledPrimaryBtn={!sendingDoc || !sendingName}
      secondaryBtnTxt={showConfirm ? null : t('dm_modal_cancel')}
      secondaryBtnAction={showConfirm ? null : onClose}
    >
      <div className={`modal-fade-wrapper ${showConfirm ? 'compact' : 'full'}`}>
        <div
          className={`modal-fade-section ${showConfirm ? 'hidden' : 'visible'}`}
        >
          <div className='row'>
            <div className='col-8'>
              {loadingEditor && <Skeleton height='450px' />}
              <Editor
                apiKey={TinyMCE_ApiKey}
                onInit={(evt, editor) => {
                  editorRef.current = editor;
                  setLoadingEditor(false);
                }}
                value={editorContent}
                onEditorChange={(c) => setEditorContent(c)}
                init={{
                  height: '65vh',
                  menubar: true,
                  plugins:
                    'advlist autolink lists link charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime table code help wordcount',
                  toolbar:
                    'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist outdent indent | table | removeformat',
                  branding: false,
                  language: EDITOR_LANG_MAP[dmLanguage] || 'es',
                }}
              />
            </div>
            <div className='col-4'>
              <Input
                label={t('dm_modal_nameLabel')}
                value={sendingName}
                onChange={(e) => setSendingName(e.target.value)}
              />
              <ComboBox
                label={t('dm_modal_caseLabel')}
                options={caseOptions}
                value={sendingCase}
                onChange={(selected) => setSendingCase(selected)}
                onInputChange={(text) => setCaseSearch(text)}
                loading={loadingCases}
                placeholder={t('dm_modal_casePlaceholder')}
                noResultText={t('dm_modal_noResults')}
                className='space-like-input'
                isClearable
              />
              <Dropdown
                label={t('dm_modal_areaLabel')}
                value={sendingArea}
                onChange={(selected) => setSendingArea(selected)}
                options={areaOptions}
                placeholder={t('dm_modal_areaPlaceholder')}
                className='space-like-input'
              />
              <Dropdown
                label={t('dm_modal_serviceLabel')}
                value={sendingService}
                onChange={(selected) => setSendingService(selected)}
                options={serviceOptions}
                placeholder={t('dm_modal_servicePlaceholder')}
                className='space-like-input'
              />
              <ComboBox
                label={t('dm_modal_documentLabel')}
                options={documentOptions}
                value={sendingDoc}
                onChange={(selected) => setSendingDoc(selected)}
                isClearable
                disabled={!sendingService}
                loading={loadingDocs}
                noResultText={t('dm_modal_noResults')}
                placeholder={
                  !sendingService
                    ? t('dm_modal_documentPlaceholderNoService')
                    : t('dm_modal_documentPlaceholder')
                }
              />
            </div>
          </div>
        </div>
        <div
          className={`modal-fade-section ${showConfirm ? 'visible' : 'hidden'}`}
        >
          <p className='caption mb-3'>
            {t('dm_modal_confirmMessage')}
          </p>
          <div className='mt-4 d-flex justify-content-end'>
            <Button
              label={t('dm_modal_confirmCancel')}
              size='sm'
              variant='secondary'
              onClick={() => setShowConfirm(false)}
              className='me-3'
            />
            <Button
              label={t('dm_modal_confirmCreate')}
              size='sm'
              variant='success'
              onClick={createAndSend}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalNewSending;
